import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { authService } from '../services/api';
import { showApiError, categorizeError, ERROR_TYPES } from '../utils/errorHandler';

function LoginForm({ onLogin, onShowSignup }) {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [networkError, setNetworkError] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // Clear network error when user starts typing
    if (networkError) setNetworkError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNetworkError(null);

    try {
      const response = await authService.login(formData);
      onLogin(response.data.user, response.data.token);
    } catch (error) {
      const errorInfo = categorizeError(error);
      
      if (errorInfo.type === ERROR_TYPES.NETWORK || errorInfo.type === ERROR_TYPES.TIMEOUT) {
        setNetworkError(errorInfo);
      } else {
        showApiError(error, 'Login');
      }
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = async (role) => {
    const credentials = {
      'superadmin': { email: 'admin@oshohimalayas.com', password: 'admin123' },
      'propertyadmin': { email: 'property@oshohimalayas.com', password: 'admin123' },
      'staff': { email: 'staff@oshohimalayas.com', password: 'admin123' },
      'guest': { email: 'guest@example.com', password: 'admin123' }
    };

    if (credentials[role]) {
      setFormData(credentials[role]);
      setNetworkError(null);
      try {
        setLoading(true);
        const response = await authService.login(credentials[role]);
        onLogin(response.data.user, response.data.token);
      } catch (error) {
        const errorInfo = categorizeError(error);
        
        if (errorInfo.type === ERROR_TYPES.NETWORK || errorInfo.type === ERROR_TYPES.TIMEOUT) {
          setNetworkError(errorInfo);
        } else {
          showApiError(error, 'Login');
        }
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="card" style={{ maxWidth: '400px', margin: '50px auto' }}>
      <h2 className="text-center mb-4">Login</h2>
      
      {/* Network Error Display */}
      {networkError && (
        <div style={{
          backgroundColor: networkError.type === 'TIMEOUT' ? '#F3E6CB' : '#F2D9D3',
          border: `1px solid ${networkError.type === 'TIMEOUT' ? '#B68235' : '#A13F2F'}`,
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '12px'
        }}>
          <span style={{ fontSize: '20px' }}>
            {networkError.type === 'TIMEOUT' ? '' : ''}
          </span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '600', color: networkError.type === 'TIMEOUT' ? '#684A1F' : '#5F241B', marginBottom: '4px' }}>
              {networkError.type === 'TIMEOUT' ? 'Request Timeout' : 'Connection Error'}
            </div>
            <div style={{ fontSize: '14px', color: networkError.type === 'TIMEOUT' ? '#825D26' : '#772D21' }}>
              {networkError.message}
            </div>
            <button
              type="button"
              onClick={() => setNetworkError(null)}
              style={{
                marginTop: '8px',
                fontSize: '12px',
                color: '#5F6B61',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            name="email"
            className="form-control"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Password</label>
          <input
            type="password"
            name="password"
            className="form-control"
            value={formData.password}
            onChange={handleChange}
            required
          />
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              flex: 1,
              backgroundColor: loading ? '#A5A89B' : '#1A3E2A',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
          
          <button
            type="button"
            onClick={onShowSignup}
            style={{
              flex: 1,
              backgroundColor: 'transparent',
              color: '#1A3E2A',
              border: '2px solid #1A3E2A',
              padding: '12px 24px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: '600',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => {
              e.target.style.backgroundColor = '#1A3E2A';
              e.target.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#1A3E2A';
            }}
          >
            Sign Up
          </button>
        </div>
      </form>

      <div style={{ marginTop: '30px', borderTop: '1px solid #E3DFD3', paddingTop: '20px' }}>
        <h4 className="text-center mb-4">Quick Login (Test Accounts)</h4>
        <div style={{ display: 'grid', gap: '10px' }}>
          <button
            className="btn btn-secondary"
            onClick={() => quickLogin('superadmin')}
            disabled={loading}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              justifyContent: 'center'
            }}
          >
            Super Admin
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => quickLogin('propertyadmin')}
            disabled={loading}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              justifyContent: 'center'
            }}
          >
            Property Admin
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => quickLogin('staff')}
            disabled={loading}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              justifyContent: 'center'
            }}
          >
            Staff
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => quickLogin('guest')}
            disabled={loading}
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '8px',
              justifyContent: 'center'
            }}
          >
            Guest
          </button>
        </div>
      </div>
    </div>
  );
}

export default LoginForm;